/**
 * Resume Ranking Profile API Service
 * Provides functionality to get resume rankings for profile display
 */

import { createAxiosInstance } from './axios';

/**
 * Gets resume ranking data for a specific user resume
 * @param {number} userResumeId - The ID of the user resume to get ranking for
 * @param {Function} getToken - Optional Clerk getToken function for authentication
 * @returns {Promise<Object>} Response containing resume ranking data
 */
export const getResumeRanking = async (userResumeId, getToken = null) => {
  try {
    const axiosInstance = createAxiosInstance(getToken);
    const response = await axiosInstance.get(`/resume-ranking/`, {
      params: {
        user_resume_id: userResumeId
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching resume ranking:', error);
    throw error;
  }
};

/**
 * Gets resume rankings for multiple resumes
 * @param {Array} resumeIds - Array of resume IDs to get rankings for
 * @param {Function} getToken - Optional Clerk getToken function for authentication
 * @returns {Promise<Object>} Object mapping resume IDs to their ranking data
 */
export const getMultipleResumeRankings = async (resumeIds, getToken = null) => {
  try {
    const rankings = {};
    
    // Fetch rankings for all resumes in parallel
    const promises = resumeIds.map(async (resumeId) => {
      try {
        const ranking = await getResumeRanking(resumeId, getToken);
        return { resumeId, ranking };
      } catch (error) {
        console.error(`Error fetching ranking for resume ${resumeId}:`, error);
        return { resumeId, ranking: null };
      }
    });
    
    const results = await Promise.all(promises);
    
    // Convert array to object mapping
    results.forEach(({ resumeId, ranking }) => {
      rankings[resumeId] = ranking;
    });
    
    return rankings;
  } catch (error) {
    console.error('Error fetching multiple resume rankings:', error);
    throw error;
  }
};

/**
 * Extracts the latest ranking data from the API response
 * @param {Object} rankingResponse - The API response from getResumeRanking
 * @returns {Object|null} The latest ranking data or null if not found
 */
export const extractLatestRanking = (rankingResponse) => {
  if (!rankingResponse || !rankingResponse.rankings || rankingResponse.rankings.length === 0) {
    return null;
  }
  
  // Get the most recent ranking (they should be sorted by created_at)
  const latestRanking = rankingResponse.rankings[0];
  return latestRanking.ranking || null;
};

/**
 * Parses gaps_compared_to_target_job from string to array
 * @param {string} gapsString - String representation of gaps array
 * @returns {Array} Array of gap strings
 */
export const parseGaps = (gapsString) => {
  if (!gapsString) return [];
  
  try {
    // Remove single quotes and parse as JSON
    const cleanedString = gapsString.replace(/'/g, '"');
    return JSON.parse(cleanedString);
  } catch (error) {
    console.error('Error parsing gaps string:', error);
    return [];
  }
};